import React, { useRef, useState, useEffect } from 'react';

const DrawingCanvas = ({ onImageCapture, onClose }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [color, setColor] = useState('#000000');
  const [lineWidth, setLineWidth] = useState(3);
  const [tool, setTool] = useState('pen'); // pen, eraser
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [description, setDescription] = useState('');

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
  }, []);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    ctx.beginPath();
    ctx.moveTo(
      e.clientX - rect.left,
      e.clientY - rect.top
    );
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';
    
    if (tool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(0,0,0,1)';
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = color;
    }
    
    ctx.lineTo(
      e.clientX - rect.left,
      e.clientY - rect.top
    );
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const captureDrawing = () => {
    setShowDescriptionModal(true);
  };

  const submitDrawing = () => {
    if (!description.trim()) {
      alert('Veuillez décrire ce que vous avez dessiné');
      return;
    }
    const canvas = canvasRef.current;
    const imageData = canvas.toDataURL('image/png');
    onImageCapture(imageData, description);
    setShowDescriptionModal(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-2xl p-6 max-w-4xl w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-white">Zone de Dessin</h3>
            <p className="text-sm text-slate-400">Dessinez la gravure que vous observez sur le verre</p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors text-2xl"
          >
            ✕
          </button>
        </div>

        {/* Toolbar */}
        <div className="mb-4 flex flex-wrap items-center gap-3 p-3 bg-slate-700/50 rounded-lg">
          {/* Tool Selection */}
          <div className="flex gap-2">
            <button
              onClick={() => setTool('pen')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                tool === 'pen'
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
              }`}
            >
              ✏️ Crayon
            </button>
            <button
              onClick={() => setTool('eraser')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                tool === 'eraser'
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
              }`}
            >
              🧹 Gomme
            </button>
          </div>

          {/* Color Picker */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-300">Couleur:</span>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="w-10 h-10 rounded cursor-pointer"
            />
          </div>

          {/* Line Width */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-300">Épaisseur:</span>
            <input
              type="range"
              min="1"
              max="20"
              value={lineWidth}
              onChange={(e) => setLineWidth(e.target.value)}
              className="w-24"
            />
            <span className="text-sm text-slate-300 w-8">{lineWidth}px</span>
          </div>

          {/* Clear Button */}
          <button
            onClick={clearCanvas}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            🗑️ Effacer tout
          </button>
        </div>

        {/* Canvas */}
        <div className="mb-4 bg-white rounded-lg overflow-hidden border-4 border-slate-600">
          <canvas
            ref={canvasRef}
            width={800}
            height={500}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            className="cursor-crosshair w-full"
            style={{ touchAction: 'none' }}
          />
        </div>

        {/* Instructions */}
        <div className="mb-4 p-3 bg-blue-600/20 border border-blue-500/30 rounded-lg">
          <p className="text-sm text-blue-300">
            💡 <strong>Conseil :</strong> Dessinez les formes, symboles, lettres et logos que vous voyez sur la gravure du verre. 
            Pas besoin d'être parfait - l'IA analysera votre dessin approximatif pour identifier le verre.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-slate-600 hover:bg-slate-500 text-white rounded-lg transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={captureDrawing}
            className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-semibold"
          >
            ✓ Analyser ce dessin
          </button>
        </div>
      </div>

      {/* Description Modal */}
      {showDescriptionModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 max-w-2xl w-full mx-4">
            <h3 className="text-xl font-bold text-white mb-4">Décrivez votre dessin</h3>
            <p className="text-sm text-slate-300 mb-4">
              Pour aider l'IA à identifier la gravure, décrivez en détail ce que vous avez dessiné : 
              formes, symboles, lettres, codes, position des éléments, etc.
            </p>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ex: J'ai dessiné un cercle avec une flèche à l'intérieur, et en dessous les lettres 'VLX'. Il y a aussi un petit carré sur le côté nasal..."
              className="w-full bg-slate-700 text-white rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              rows="6"
              autoFocus
            />
            <div className="flex gap-3 justify-end mt-4">
              <button
                onClick={() => setShowDescriptionModal(false)}
                className="px-6 py-3 bg-slate-600 hover:bg-slate-500 text-white rounded-lg transition-colors"
              >
                Retour
              </button>
              <button
                onClick={submitDrawing}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-semibold"
              >
                ➤ Envoyer à l'IA
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DrawingCanvas;
